// The offline/failure fallback for dynamic translations.
//
// WHY THIS IS HOST-AGNOSTIC (Notion item 12)
// ------------------------------------------
// LanguageContext had two cache fallbacks and both were gated on an
// Electron-only capability flag. Capacitor, which is the actual POS
// runtime, had neither. One failed `/translations` call at startup therefore
// left `translationsLoaded = false` and every string on the till rendered as
// its raw key (`search_products_placeholder`, `clear_basket`, `charge`)
// until somebody relaunched the app. Observed live during the 8 Sep session
// when an ETL run slowed the backend.
//
// There was a second, quieter cost. The comment on the outer fallback says
// it exists so a failure does not leave the retry interval "hammering the
// API with repeated 401s". Native runs that same interval, so a struggling
// backend was being hammered by every till at once — the one branch that
// most needed the protection was the branch without it.
//
// Extracted here rather than inlined so the behaviour is testable without
// mounting the whole context, and so the two call sites cannot drift apart.
//
// Staleness across tenants is not a concern this module has to solve:
// AuthContext removes CACHE_KEY on logout (context/AuthContext.js), so a
// cache never outlives the session that filled it.

export const CACHE_KEY = 'cached_translations';

/**
 * The cached translation rows, or null when there is nothing usable.
 *
 * Never throws. This runs on a boot path, and localStorage is writable by
 * other code and other tabs — a corrupt value must cost us the translations,
 * not the application.
 *
 * @returns {Array|null}
 */
export function readCachedTranslations() {
    try {
        const raw = localStorage.getItem(CACHE_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        // An empty list is treated as no cache: falling back to it would set
        // translationsLoaded=true with nothing in it, which renders raw keys
        // anyway but stops us retrying — strictly worse than no cache.
        if (!Array.isArray(parsed) || parsed.length === 0) return null;
        return parsed;
    } catch (err) {
        return null;
    }
}

/**
 * Cache the rows for the next failed fetch. Best-effort: a full or disabled
 * localStorage must not fail the successful path that just got real data.
 *
 * @param {Array} rows
 */
export function writeCachedTranslations(rows) {
    try {
        localStorage.setItem(CACHE_KEY, JSON.stringify(rows));
    } catch (err) {
        // Quota or a privacy mode that refuses writes. Nothing to do — the
        // caller already has live translations; only the fallback is lost.
    }
}

/**
 * Rows -> the two maps the context renders from.
 *
 * @param {Array} rows
 * @returns {{translationMap: Object, detailsMap: Object}}
 */
export function toTranslationMaps(rows) {
    const translationMap = {};
    const detailsMap = {};
    if (!Array.isArray(rows)) return { translationMap, detailsMap };
    rows.forEach((row) => {
        if (!row || !row.english) return;
        translationMap[row.english] = row.translated_english;
        // Only set when present. An `undefined` entry would later read as a
        // key that HAS details rather than one that lacks them.
        if (row.translation_details) detailsMap[row.english] = row.translation_details;
    });
    return { translationMap, detailsMap };
}
